/**
 * Single source of truth for void reasons.
 *
 * Used by both the admin-PIN void modal and the void log filter so the
 * dropdown values always match what gets persisted in `void_log.reason`.
 */
export const VOID_REASONS = [
  'Customer Request',
  'Kitchen Error',
  'Wrong Order',
  'Manager Decision',
  'Quality Issue',
  'Other',
] as const

export type VoidReason = (typeof VOID_REASONS)[number]

/** The one reason that carries no information on its own. */
export const OTHER_REASON: VoidReason = 'Other'

/**
 * The reasons offered as one-tap chips in the void modal.
 *
 * "Other" is deliberately absent: in the modal its place is taken by the
 * free-text field, so a server types what happened instead of picking a label
 * that says nothing. It stays in VOID_REASONS because the void-log filter must
 * still be able to select it — both for rows written before this field existed
 * and for the "Other: ..." rows written since.
 */
export const PRESET_REASONS = VOID_REASONS.filter(
  (r): r is Exclude<VoidReason, typeof OTHER_REASON> => r !== OTHER_REASON,
)

/** Free-text notes are capped so a stray paste can't bloat the audit row. */
export const VOID_NOTE_MAX = 120

/**
 * Build the string that gets persisted in `void_log.reason`.
 *
 * A bare "Other" tells whoever reads the void log nothing, which is the whole
 * point of the note. So "Other" is stored WITH its note, prefixed:
 *
 *     composeVoidReason('Other', 'guest spilled the tray')
 *       -> 'Other: guest spilled the tray'
 *
 * Keeping the category prefix (rather than storing the bare note) means the
 * void-log Reason filter still groups these rows under "Other" — the backend
 * matches `reason = 'Other'` OR `reason LIKE 'Other: %'`. `void_log` is
 * append-only, so this format is what future readers will see forever; don't
 * change the separator without a migration plan for existing rows.
 */
export function composeVoidReason(reason: VoidReason, note: string): string {
  if (reason !== OTHER_REASON) return reason
  const trimmed = note.trim()
  return trimmed ? `${OTHER_REASON}: ${trimmed}` : OTHER_REASON
}

// ── F-CTRL-N74: was it made? ────────────────────────────────────────────────
//
// Every void of an item the kitchen already has now says which kind it was:
// the customer changed their mind before anything was made, or the food was
// made and thrown away. The second takes the recipe's ingredients out of
// stock as waste. Each kind has its own reasons.
//
// THE list, mirrored by voidReasons in backend/internal/handlers/void_waste.go
// (TestVoidReasons_MatchFrontend_FCTRLN74 compares the two). The label is what
// void_log.reason stores.

export type VoidOutcome = 'not_made' | 'wasted'

export interface OutcomeReason {
  code: string
  label: string
}

export const VOID_OUTCOME_REASONS: Record<VoidOutcome, readonly OutcomeReason[]> = {
  not_made: [
    { code: 'changed_mind', label: 'Customer changed mind' },
    // F-FE-N193: the reason an online order loses an item after Accept.
    { code: 'out_of_stock', label: 'Out of stock' },
    { code: 'wrong_entry', label: 'Wrong item entered' },
    { code: 'duplicate', label: 'Duplicate' },
    { code: 'other', label: 'Other' },
  ],
  wasted: [
    { code: 'changed_mind', label: 'Customer changed mind' },
    { code: 'wrong_item_made', label: 'Wrong item made' },
    { code: 'quality_issue', label: 'Quality issue / burnt' },
    { code: 'dropped', label: 'Dropped' },
    { code: 'too_slow', label: 'Took too long' },
    { code: 'other', label: 'Other' },
  ],
}

export const OTHER_CODE = 'other'

export const VOID_OUTCOME_LABEL: Record<VoidOutcome, string> = {
  not_made: 'Not made',
  wasted: 'Made, thrown away',
}

export const VOID_OUTCOME_HELP: Record<VoidOutcome, string> = {
  not_made: 'Nothing was cooked. No stock is used.',
  wasted: 'It was cooked and binned. Its ingredients are taken out of stock as waste.',
}

/** The chips for an outcome: every reason but Other (the text field is Other). */
export function outcomePresets(outcome: VoidOutcome): OutcomeReason[] {
  return VOID_OUTCOME_REASONS[outcome].filter((r) => r.code !== OTHER_CODE)
}

/**
 * What to pre-select from the kitchen status. Once the kitchen has started,
 * the food exists. A line that is only 'sent' is left for staff to answer:
 * kitchens that work from printed tickets never move past 'sent', so it says
 * nothing about whether the dish was made.
 */
export function suggestedVoidOutcome(itemStatus: string | null | undefined): VoidOutcome | null {
  if (itemStatus === 'preparing' || itemStatus === 'ready' || itemStatus === 'served') return 'wasted'
  return null
}

/** The void_log.reason for a code, with the typed note when it is Other. */
export function composeOutcomeReason(outcome: VoidOutcome, code: string, note: string): string {
  if (code === OTHER_CODE) {
    const t = note.trim()
    return t ? `Other: ${t}` : 'Other'
  }
  return VOID_OUTCOME_REASONS[outcome].find((r) => r.code === code)?.label ?? ''
}

/** Every reason label a void can carry, old and new, for the Void Log filter. */
export const ALL_VOID_REASON_LABELS: string[] = Array.from(
  new Set<string>([
    ...VOID_OUTCOME_REASONS.not_made.map((r) => r.label),
    ...VOID_OUTCOME_REASONS.wasted.map((r) => r.label),
    ...VOID_REASONS,
  ]),
)
